import './verificationDocumentListPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { CheckCircleRoundedIcon } from '../../../components/Icons/CheckCircleRoundedIcon';
import { NavigateNextRoundedIcon } from '../../../components/Icons/NavigateNextRoundedIcon';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { VerificationLink } from '../../../components/VerificationLink/VerificationLink';
import useDashboardStore from '../../../store/dashboardStore/useDashboardStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { VerificationStatus } from '../../../store/verificationStore/enums/VerificationStatus';
import { useVerificationLinks } from '../../../store/verificationStore/hooks/useVerificationLinks';
import useVerificationStore from '../../../store/verificationStore/useVerificationStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: { vehicleId },
  } = useDashboardStore();

  const {
    computed: { verifications, progress },
  } = useVerificationStore();

  const { links } = useVerificationLinks(verifications);

  const previousStep = (): void => {
    navigate(`/verifications/${vehicleId}`);
  };

  return (
    <div className="verification-document-list-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            progress={progress}
            showDrawerToggle
            onBackClick={previousStep}
          />

          <main>
            <form
              data-testid={locator.pages.verificationDocumentList.form.root}
              noValidate
            >
              <div className="form-content">
                <h1
                  data-testid={locator.pages.verificationDocumentList.form.h1}
                >
                  {t('VerificationDocumentListPage.title')}
                </h1>
                <h2
                  data-testid={locator.pages.verificationDocumentList.form.h2}
                >
                  {t('VerificationDocumentListPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <ul>
                    {links.map(
                      ({ name, status, link }, index): ReactNode => (
                        <li key={name}>
                          <VerificationLink
                            className={
                              status === VerificationStatus.COMPLETE
                                ? 'complete'
                                : ''
                            }
                            link={link}
                            testId={locator.pages.verificationDocumentList.form.getVerificationLink(
                              index,
                            )}
                          >
                            <div>
                              <div className="title">
                                <h3>{name}</h3>
                                <span>{t(`verificationStatus.${status}`)}</span>
                              </div>
                              {status === VerificationStatus.COMPLETE ? (
                                <CheckCircleRoundedIcon />
                              ) : (
                                <NavigateNextRoundedIcon />
                              )}
                            </div>
                          </VerificationLink>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
              <footer
                data-testid={locator.pages.verificationDocumentList.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'VerificationDocumentListPage';
